/**
 * Utility functions for animation configuration
 */

import type { CSSProperties } from 'react';
import type { AnimationConfig } from './types';

/**
 * Generates CSS custom properties object for animations
 *
 * @example
 * ```typescript
 * const styles = getVarsObject({ opacityStart: 0, translateY: '20px' });
 * // Returns: { "--anim-opacity-start": "0", "--anim-translate-y": "20px" }
 * ```
 */
export function getVarsObject(config: AnimationConfig): CSSProperties {
  const styles: Record<string, string> = {};

  if (config.duration !== undefined) {
    styles['--anim-duration'] = `${config.duration}s`;
  }

  if (config.delay !== undefined) {
    styles['--anim-delay'] = `${config.delay}s`;
  }

  if (config.timing !== undefined) {
    styles['--anim-timing'] = config.timing;
  }

  if (config.translateX !== undefined) {
    styles['--anim-translate-x'] = config.translateX;
  }

  if (config.translateY !== undefined) {
    styles['--anim-translate-y'] = config.translateY;
  }

  if (config.scale !== undefined) {
    styles['--anim-scale'] = config.scale.toString();
  }

  if (config.rotate !== undefined) {
    styles['--anim-rotate'] = config.rotate;
  }

  if (config.blur !== undefined) {
    styles['--anim-blur'] = config.blur;
  }

  if (config.opacityStart !== undefined) {
    styles['--anim-opacity-start'] = config.opacityStart.toString();
  }

  if (config.opacityEnd !== undefined) {
    styles['--anim-opacity-end'] = config.opacityEnd.toString();
  }

  return styles;
}

/**
 * Generates CSS custom properties string for animations
 *
 * @example
 * ```typescript
 * const style = getVarsStyle({ opacityStart: 0, translateY: '20px' });
 * // Returns: "--anim-opacity-start: 0; --anim-translate-y: 20px"
 * ```
 */
export function getVarsStyle(config: AnimationConfig): string {
  const styles = getVarsObject(config);
  return Object.entries(styles)
    .map(([key, value]) => `${key}: ${value}`)
    .join('; ');
}

/**
 * Helper to combine multiple animation configs
 *
 * @example
 * ```typescript
 * const combined = mergeConfigs(
 *   presets.fadeIn(),
 *   { blur: '5px', duration: 0.8 }
 * );
 * ```
 */
export function mergeConfigs(...configs: AnimationConfig[]): AnimationConfig {
  return configs.reduce((acc, config) => ({ ...acc, ...config }), {});
}

/**
 * Create staggered delays for list items
 *
 * @example
 * ```typescript
 * const staggered = mergeConfigs(
 *   presets.slideUp(),
 *   stagger(index, 0.1)
 * );
 * ```
 */
export function stagger(
  index: number,
  delayIncrement: number,
  baseDelay = 0
): AnimationConfig {
  return {
    delay: baseDelay + index * delayIncrement,
  };
}

export function getOnLoadClass(className?: string): string {
  return `${className ? `${className} ` : ''}css-motion css-motion--load`;
}

export function getOnViewClass(className?: string): string {
  return `${className ? `${className} ` : ''}css-motion css-motion--view`;
}
